import { useEffect, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip } from "./chip";
import {
  CHANNEL_LABEL,
  SLOT_STATUS_CLASS,
  SLOT_STATUS_LABEL,
  channelLabel,
  fmtShortDay,
  orderDateFor,
  parseDayKey,
  updateTargetLabel,
  type Plan,
  type PlanSlot,
} from "./agentic";
import { dayKey } from "./helpers";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="break-words text-sm text-foreground">{children}</dd>
    </div>
  );
}

/** When Draper will order a still-planned slot, in one sentence. */
export function orderingSentence(slot: Pick<PlanSlot, "date">, now: Date = new Date()): string {
  const orderOn = orderDateFor(slot.date);
  const today = parseDayKey(dayKey(now));
  const slotDay = parseDayKey(slot.date.slice(0, 10));
  if (slotDay.getTime() < today.getTime()) return "This slot's date has passed and it was never ordered.";
  if (orderOn.getTime() <= today.getTime()) return "Draper orders this automatically, and it is due to be ordered in the next check.";
  return `Draper will order this on ${orderOn.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}.`;
}

/**
 * One slot of a content plan: what, when, where, and why. Opened from the Calendar (read-only, with Skip) and from the
 * Plan tab (editable topic / angle while the plan is not approved).
 */
export function SlotDialog({
  open,
  onOpenChange,
  slot,
  plan,
  editable,
  saving,
  onSave,
  onSkip,
  onOpenItem,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slot: PlanSlot | null;
  plan: Plan | null;
  /** Topic and angle can be changed (plan is a draft or reviewed, and the slot is still just planned). */
  editable: boolean;
  saving?: boolean;
  onSave?: (patch: { topic: string; angle: string }) => void;
  onSkip?: () => void;
  onOpenItem?: (id: number) => void;
}) {
  const [topic, setTopic] = useState("");
  const [angle, setAngle] = useState("");
  const [confirmSkip, setConfirmSkip] = useState(false);

  useEffect(() => {
    setTopic(slot?.topic ?? "");
    setAngle(slot?.angle ?? "");
    setConfirmSkip(false);
  }, [slot?.slotId, slot?.topic, slot?.angle, open]);

  if (!slot) return null;
  const status = slot.status;
  // The server only lets draft and reviewed plans be changed, so a slot of an approved plan cannot be skipped.
  const canSkip = status === "planned" && !!plan && (plan.status === "draft" || plan.status === "reviewed") && !!onSkip;
  const dirty = topic.trim() !== slot.topic || angle.trim() !== (slot.angle ?? "");
  const ch = channelLabel(slot.channel);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="slot-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6 text-left leading-snug">{editable ? "Edit this slot" : slot.topic || "Planned piece"}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-1.5 pt-1 text-left">
            <Chip className="border-border bg-muted/40 text-foreground/80">{ch}</Chip>
            <Chip className="border-border bg-muted/40 text-foreground/80">{fmtShortDay(slot.date)}</Chip>
            <Chip className="border-border bg-muted/40 text-foreground/80">{slot.kind === "update" ? "Update of an existing piece" : "New piece"}</Chip>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${SLOT_STATUS_CLASS[status]}`} data-testid="slot-status">
              {SLOT_STATUS_LABEL[status]}
            </span>
          </DialogDescription>
        </DialogHeader>

        {editable ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="slot-topic" className="text-xs font-medium text-muted-foreground">Topic</label>
              <Input id="slot-topic" value={topic} maxLength={300} onChange={(e) => setTopic(e.target.value)} data-testid="input-slot-topic" />
            </div>
            <div className="space-y-1">
              <label htmlFor="slot-angle" className="text-xs font-medium text-muted-foreground">Angle</label>
              <Textarea id="slot-angle" value={angle} rows={4} maxLength={1200} onChange={(e) => setAngle(e.target.value)} data-testid="input-slot-angle" />
            </div>
            {(slot.targetQuery || slot.kind === "update") && (
              <dl className="space-y-3">
                {slot.targetQuery && <Field label="Target search query">{slot.targetQuery}</Field>}
                {slot.kind === "update" && <Field label="Updates">{updateTargetLabel(slot) ?? "An existing post"}</Field>}
              </dl>
            )}
          </div>
        ) : (
          <dl className="space-y-3" data-testid="slot-details">
            {slot.topic && <Field label="Topic">{slot.topic}</Field>}
            <Field label="Angle">{slot.angle || <span className="text-muted-foreground">Not set</span>}</Field>
            <Field label="Target search query">{slot.targetQuery || <span className="text-muted-foreground">Not set</span>}</Field>
            <Field label="Channel">{CHANNEL_LABEL[slot.channel as keyof typeof CHANNEL_LABEL] ?? ch}</Field>
            {slot.kind === "update" && <Field label="Updates">{updateTargetLabel(slot) ?? "An existing post"}</Field>}
          </dl>
        )}

        {status === "planned" && !editable && (
          <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground/90" data-testid="slot-ordering">
            {plan?.status === "approved" ? orderingSentence(slot) : "Nothing is ordered until the plan is approved."}
            {plan?.status === "approved" && (
              <span className="mt-1 block text-xs text-muted-foreground" data-testid="slot-fixed-note">
                The slots of an approved plan are fixed. To drop this one, archive the plan and approve a new one.
              </span>
            )}
          </p>
        )}
        {status === "ordered" && (
          <p className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-100">Draper's team is writing this now. It appears here when it is ready.</p>
        )}
        {status === "skipped" && <p className="text-sm text-muted-foreground">This slot was skipped, so nothing will be written for it.</p>}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between sm:space-x-0">
          <div className="flex flex-wrap gap-2">
            {slot.contentItemId != null && onOpenItem && (
              <Button type="button" variant="outline" onClick={() => onOpenItem(slot.contentItemId!)} data-testid="button-slot-open-item">
                Open the piece <ExternalLink size={14} />
              </Button>
            )}
            {canSkip &&
              (confirmSkip ? (
                <>
                  <Button type="button" variant="destructive" onClick={onSkip} disabled={saving} data-testid="button-slot-confirm-skip">
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    Yes, skip it
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setConfirmSkip(false)}>Keep it</Button>
                </>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmSkip(true)} data-testid="button-slot-skip">
                  Skip this slot
                </Button>
              ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {editable ? "Cancel" : "Close"}
            </Button>
            {editable && (
              <Button
                type="button"
                disabled={!dirty || !topic.trim() || saving}
                onClick={() => onSave?.({ topic: topic.trim(), angle: angle.trim() })}
                data-testid="button-slot-save"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Save
              </Button>
            )}
          </div>
        </DialogFooter>
        {confirmSkip && <p className="text-xs text-muted-foreground">Skipping means Draper writes nothing for this slot. You can't undo it from here.</p>}
      </DialogContent>
    </Dialog>
  );
}
